/**
 * Reading an invitation out of whatever carried it to the device, and deciding
 * whether the one the device is holding still counts.
 *
 * The invitation email links to the web app's `/join?code=` (apps/server
 * invite-mail.ts). On a phone that link arrives three ways: as a Universal or
 * App Link on the web origin, as the app's own `recordos://join?code=`, or,
 * after a fresh install from the Play Store, as `invite=` inside the install
 * referrer. Each reader answers the token or null, and never throws: a link
 * that is not an invitation is not an error, it is simply not ours.
 *
 * Lives here so the web app and the phone read a link the same way.
 */

/** The invitation waiting on the device until the person joins. */
export type PendingInvite = {
  token: string;
  /** The emailed link's inbox proof, when the link carried one. */
  proof?: string | null;
  /** ISO 8601, from the preview; null until the preview has answered. */
  expiresAt: string | null;
};

const APP_SCHEME = 'recordos:';

const cleanToken = (value: string | null | undefined): string | null => {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

/**
 * The code of a `/join?code=` link on any origin, or of `recordos://join?code=`.
 * Null for any other path, a link without a code, or text that is not a URL.
 */
export const inviteTokenFromUrl = (url: string | null | undefined): string | null => joinParams(url)?.get('code') ?? null;

/**
 * The `proof` beside the code of a `/join` link, which only the invitation
 * email carries. Null when the link has none or is not an invitation.
 */
export const inviteProofFromUrl = (url: string | null | undefined): string | null =>
  inviteTokenFromUrl(url) === null ? null : (joinParams(url)?.get('proof') ?? null);

/** The query of a `/join` link, or null when the text is not one. */
function joinParams(url: string | null | undefined): { get: (key: string) => string | null } | null {
  if (url === null || url === undefined || url.trim() === '') return null;
  let parsed: URL;
  try {
    parsed = new URL(url.trim());
  } catch {
    return null;
  }
  // `recordos://join?code=` parses with `join` as the host and an empty path;
  // `recordos:///join?code=` parses with it as the path. Both are the link.
  const isJoin =
    parsed.protocol === APP_SCHEME
      ? `${parsed.host}${parsed.pathname}`.replace(/^\/+|\/+$/g, '') === 'join'
      : (parsed.protocol === 'https:' || parsed.protocol === 'http:') &&
        parsed.pathname.replace(/\/+$/, '') === '/join';
  if (!isJoin) return null;
  return { get: (key) => cleanToken(parsed.searchParams.get(key)) };
}

/**
 * The `invite=` of a Play install referrer, such as
 * `utm_source=invite&invite=abc`, whether or not the whole string arrived
 * url-encoded. Null for a referrer that carries no invite.
 */
export const inviteTokenFromReferrer = (referrer: string | null | undefined): string | null =>
  referrerParams(referrer)?.get('invite') ?? null;

/** The `proof=` beside `invite=` in a Play install referrer, or null. */
export const inviteProofFromReferrer = (referrer: string | null | undefined): string | null =>
  inviteTokenFromReferrer(referrer) === null ? null : (referrerParams(referrer)?.get('proof') ?? null);

function referrerParams(referrer: string | null | undefined): { get: (key: string) => string | null } | null {
  if (referrer === null || referrer === undefined || referrer.trim() === '') return null;
  let text = referrer.trim();
  // An encoded referrer has no bare `=` or `&`; decode it once to read it.
  if (!text.includes('=') && /%3d/i.test(text)) {
    try {
      text = decodeURIComponent(text);
    } catch {
      return null;
    }
  }
  try {
    const params = new URLSearchParams(text.replace(/^\?/, ''));
    return { get: (key) => cleanToken(params.get(key)) };
  } catch {
    return null;
  }
}

/**
 * Whether the invitation on the device still counts at `now`. One with no
 * known expiry counts until the preview says otherwise; one whose expiry is
 * unreadable does not.
 */
export const pendingInviteLive = (pending: PendingInvite | null | undefined, now: Date): boolean => {
  if (pending === null || pending === undefined) return false;
  if (cleanToken(pending.token) === null) return false;
  if (pending.expiresAt === null) return true;
  const ends = Date.parse(pending.expiresAt);
  if (Number.isNaN(ends)) return false;
  return ends > now.getTime();
};
